import React from "react"
import { theme } from "../../../theme"
import styled, { css } from "styled-components"
import { TextSmall } from "../../atoms/text"

export type ProgressBarMiniProps = {
  label: string
  state?: ProgressBarMiniState
  currentStepPosition?: number
  totalNumberOfSteps?: number
  hideStep?: boolean
  className?: string
  dataCy?: string
}

export enum ProgressBarMiniState {
  Nominal = "nominal",
  Urgent = "urgent",
  Halted = "halted",
}

const getProgressBarColor = (state: ProgressBarMiniState) => {
  switch (state) {
    case ProgressBarMiniState.Nominal:
      return theme.color.green9
    case ProgressBarMiniState.Urgent:
      return theme.color.colorWithOpacity(theme.color.red2, 20)
    default:
      return theme.color.grey2
  }
}

const getProgressStepColor = (state: ProgressBarMiniState) => {
  switch (state) {
    case ProgressBarMiniState.Nominal:
      return theme.color.blue1
    case ProgressBarMiniState.Urgent:
      return theme.color.red2
    default:
      return theme.color.grey4
  }
}

export const ProgressBarMini = (props: ProgressBarMiniProps) => {
  const {
    className,
    dataCy,
    label,
    state = ProgressBarMiniState.Nominal,
    currentStepPosition = 1,
    totalNumberOfSteps = 5,
    hideStep = false,
  } = props

  const totalNumberOfStepsMoreThanZero = totalNumberOfSteps < 1 ? 1 : totalNumberOfSteps
  const currentStepPositionMoreThanZero = currentStepPosition < 1 ? 1 : currentStepPosition
  const currentStepPositionMaxAndMinCapped =
    currentStepPositionMoreThanZero > totalNumberOfStepsMoreThanZero
      ? totalNumberOfStepsMoreThanZero
      : currentStepPositionMoreThanZero

  return (
    <ProgressBar className={className} data-cy={dataCy}>
      <TextSmall>{label}</TextSmall>
      <ProgressBarStyle
        $state={state}
        $totalNumberOfStepsMoreThanZero={totalNumberOfStepsMoreThanZero}
        $currentStepPositionMaxAndMinCapped={currentStepPositionMaxAndMinCapped}
        $hideStep={hideStep}
      >
        <ProgressStep $state={state} $hideStep={hideStep}></ProgressStep>
      </ProgressBarStyle>
    </ProgressBar>
  )
}

const ProgressBar = styled.div`
  width: 100%;
`

const ProgressBarStyle = styled.div<{
  $state: ProgressBarMiniState
  $totalNumberOfStepsMoreThanZero: number
  $currentStepPositionMaxAndMinCapped: number
  $hideStep: boolean
}>`
  --sizeOfStep: calc(100% / ${(p) => p.$totalNumberOfStepsMoreThanZero});
  --marginLeft: calc(var(--sizeOfStep) * (${(p) => p.$currentStepPositionMaxAndMinCapped} - 1));
  --filledArea: calc(var(--sizeOfStep) ${(p) => !p.$hideStep && "/ 2"} + var(--marginLeft));
  margin-top: ${theme.spacing.s};
  border-radius: 34px;
  height: ${theme.height.xs};
  background: linear-gradient(
    90deg,
    ${(p) =>
      css`
        ${getProgressBarColor(p.$state)} var(--filledArea) ,${theme.color.white2} 0
      `}
  );
`
const ProgressStep = styled.div<{ $state: ProgressBarMiniState; $hideStep: boolean }>`
  ${(p) => p.$hideStep && `display: none;`}
  width: var(--sizeOfStep);
  height: ${theme.height.xs};
  border-radius: 34px;
  margin-left: var(--marginLeft);
  background-color: ${(p) => getProgressStepColor(p.$state)};
`
